// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Collaborations.js

import React from 'react';
import { FiGlobe, FiExternalLink, FiUsers } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

// Helper function to resolve the partner logo safely
const getImage = (item, apiBaseURL) => {
  const candidate = item.logo || item.image || item.imageUrl || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

// Collaboration Card Component
const CollaborationCard = ({ item, apiBaseURL }) => {
  const logoSrc = getImage(item, apiBaseURL);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  return (
    <div 
      className="group bg-white rounded-2xl p-6 text-center border transition-all duration-500 hover:shadow-xl hover:-translate-y-1"
      style={{ borderColor: `${primary}15` }}
    >
      {/* Logo */}
      <div className="flex items-center justify-center h-20 mb-4">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={item.name}
            className="h-16 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
            }}
          >
            <FiGlobe className="w-8 h-8" style={{ color: secondary }} />
          </div>
        )}
      </div>

      {/* Name */}
      <h4 
        className="text-lg font-bold mb-1"
        style={{ 
          color: '#1F2937',
          fontFamily: fontHeading,
        }}
      >
        {item.name}
      </h4>

      {/* Type Badge */}
      {item.type && (
        <div 
          className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3"
          style={{ 
            backgroundColor: `${primary}15`,
            color: primary,
          }}
        >
          {item.type}
        </div>
      )}

      {/* Country */}
      {item.country && (
        <p className="text-sm mb-2" style={{ color: '#6B7280' }}>
          {item.flag || '🌍'} {item.country}
        </p>
      )}

      {/* Since */}
      {item.since && (
        <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>
          Since {item.since}
        </p>
      )}

      {/* Description */}
      {item.description && (
        <p 
          className="text-sm leading-relaxed mb-4"
          style={{ 
            color: '#6B7280',
            fontFamily: fontBody,
          }}
        >
          {item.description}
        </p>
      )}

      {/* Link */}
      {item.link && (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm transition-colors group/link"
          style={{ color: secondary }}
        >
          <span>Visit Website</span>
          <FiExternalLink className="w-3 h-3 transition-transform group-hover/link:translate-x-0.5" />
        </a>
      )}
    </div>
  );
};

// Main Collaborations Component
const Collaborations = ({ collaborations, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'grid', items = [] } = collaborations || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  return (
    <section id="collaborations" className="py-16 md:py-24 bg-gray-50/30">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium tracking-[0.15em] uppercase mb-4"
                style={{ 
                  color: secondary,
                  backgroundColor: `${secondary}15`,
                  fontFamily: fontBody,
                }}
              >
                <FiUsers className="w-3.5 h-3.5" />
                Global Partners
              </span>
              
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}

          {/* Collaborations Grid */}
          <div className={`grid gap-6 ${
            displayStyle === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1 md:grid-cols-4 gap-4'
          }`}>
            {items.map((item) => (
              <CollaborationCard 
                key={item._id || item.id || item.name} 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Collaborations;